import { CheckCircle, Clock, FileText, Target } from '@phosphor-icons/react'
import { useContext } from 'react'
import { Helmet } from 'react-helmet'
import { useHistory, useParams } from 'react-router-dom'

import { Skeleton } from '@/components/Skeleton'
import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import useSearchParams from '@/hooks/useSearchParams'
import { useLoadAnalyticsById } from '@/queries/form-analytics/useLoadAnalyticsById'
import Ui from '@/ui'
import BreadcrumbButton from '@/ui/atoms/BreadcrumbButton'
import Breadcrumbs from '@/ui/atoms/Breadcrumbs'
import PageWrapper from '@/ui/atoms/PageWrapper'
import PageTitle from '@/ui/molecules/PageTitle'
import cn from '@/utils/tailwind/cn'
import { ZentakeContext } from '@/utils/ZentakeContext'

import { CompletionChart } from './components/CompletionChart/CompletionChart'
import { CustomPeriod } from './components/CustomPeriod'
import { DeviceType } from './components/DeviceType'
import { FormsAnalyticsCard } from './components/FormsAnalyticsCard'
import { TopDropoff } from './components/TopDropoff'
import { formatDuration } from './UtilsFormsAnalytics/formatDuration'
import { getCompletionRateChange } from './UtilsFormsAnalytics/getCompletionRateChange'
import { getDateRangeByPeriod } from './UtilsFormsAnalytics/getDateRangeByPeriod'

export const FormsAnalyticsPage = () => {
  const { teamDetails } = useContext(ZentakeContext)

  const history = useHistory()

  const { resourceId } = useParams<{ resourceId: string }>()
  const updateParams = useUpdateURLSearchParams()
  const searchParams = useSearchParams()

  const period = searchParams.get('period') || 'month'
  const startDate = searchParams.get('start_date')
  const endDate = searchParams.get('end_date')

  const {
    created_before,
    created_after,
    past_period,
    previous_created_after,
    previous_created_before,
  } = getDateRangeByPeriod(period, startDate, endDate)

  const { data: analytics, isLoading } = useLoadAnalyticsById<'analytics'>({
    form: resourceId,
    created_after,
    created_before,
  })

  const { data: previousAnalytics } = useLoadAnalyticsById<'analytics'>({
    form: resourceId,
    created_after: previous_created_after,
    created_before: previous_created_before,
  })

  const { change, increased } = getCompletionRateChange(
    analytics?.completions_rate || 0,
    previousAnalytics?.completions_rate || 0
  )

  const onSelectOption = ({ id }: { id: string }) =>
    updateParams(['period', id])

  const currentPage = period

  const allowedTabs = [
    {
      id: 'week',
      title: 'This Week',
    },
    {
      id: 'month',
      title: 'This Month',
    },
    {
      id: 'all-time',
      title: 'All Time',
    },
    {
      id: 'custom',
      title: <CustomPeriod />,
    },
  ]

  const cards = [
    {
      title: 'Total Form Starts',
      subtitle: past_period,
      icon: <FileText />,
      metric: analytics?.total || 0,
    },
    {
      title: 'Completions',
      subtitle: 'Total completed forms',
      metric: analytics?.completions || 0,
      icon: <Target />,
    },
    {
      title: 'Completion Rate',
      subtitle: (
        <span className={cn('text-green-500', { 'text-red-500': !increased })}>
          {change}
        </span>
      ),
      metric: ((analytics?.completions_rate || 0) / 100).toLocaleString(
        'en-US',
        {
          style: 'percent',
          minimumFractionDigits: 0,
        }
      ),
      icon: <CheckCircle />,
    },
    {
      title: 'Average Time to Complete',
      subtitle: 'Minutes : Seconds',
      metric: formatDuration(analytics?.avg_completion_time || 0),
      icon: <Clock />,
    },
  ]

  const currentSelectedTabs = allowedTabs.findIndex(
    (item) => item.id === currentPage
  )

  return (
    <PageWrapper className="pb-11">
      <Helmet>
        <title>Analytics/Zentake</title>
      </Helmet>
      <Breadcrumbs className="!-mt-2 mb-2 !px-0">
        <BreadcrumbButton
          className="!m-0 !p-0"
          onClick={() => history.goBack()}
        >
          Back
        </BreadcrumbButton>
      </Breadcrumbs>
      <PageTitle className="!mb-4 text-gray-900" text="Analytics" />
      <Skeleton className="mb-4 !h-[22px]" {...{ isLoading }}>
        <h2 className="mb-4 text-xl font-bold text-gray-600">
          {analytics?.form_name}
        </h2>
      </Skeleton>
      <Ui.Tabs
        initialSelectedIndex={currentSelectedTabs}
        onSelectOption={onSelectOption}
        options={allowedTabs}
      />
      <div className="mx-auto flex w-full max-w-screen-xl flex-col gap-3 pt-2.5 lg:flex lg:items-center">
        <div className="grid w-full grid-cols-1 gap-7 md:grid-cols-4">
          {cards.map((card) => (
            <FormsAnalyticsCard.Root key={card.title}>
              <FormsAnalyticsCard.Icon>{card.icon}</FormsAnalyticsCard.Icon>
              <FormsAnalyticsCard.TitleMinor>
                {card.title}
              </FormsAnalyticsCard.TitleMinor>
              <Skeleton {...{ isLoading }} className="mt-1 !h-8">
                <FormsAnalyticsCard.Metric>
                  {card.metric}
                </FormsAnalyticsCard.Metric>
              </Skeleton>
              <Skeleton {...{ isLoading }}>
                <FormsAnalyticsCard.Subtitle>
                  {card.subtitle}
                </FormsAnalyticsCard.Subtitle>
              </Skeleton>
            </FormsAnalyticsCard.Root>
          ))}
        </div>
        <CompletionChart className="mt-3" />
        {teamDetails?.analytics_abandonment ? <TopDropoff /> : null}
        <DeviceType />
      </div>
    </PageWrapper>
  )
}
